import { useEffect } from 'react';
import { FieldError, useFormContext } from 'react-hook-form';
import cn from 'classnames';
// Hooks
import { useStreamImageUpload } from 'hooks';
// Ui
import Text from 'ui3/Text/Text';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Components
import UploadImagePlaceholder from 'components/ProfileSetup/components/common/UploadImagePlaceholder/UploadImagePlaceholder';
import EasyImageCrop from 'components/common3/EasyImageCrop/EasyImageCrop';
// Styles
import styles from './PosterUploader.module.scss';

type PosterFormFields = {
  image: File | null;
  previewImage: string;
};

type PosterUploaderProps = {
  alt: string;
  label?: string;
};

const PosterUploader = ({ alt, label }: PosterUploaderProps): JSX.Element => {
  const {
    register,
    errors,
    setValue,
    watch,
  } = useFormContext<PosterFormFields>();

  useEffect(() => {
    register({ name: 'image' });
    register({ name: 'previewImage' });
  }, [register]);

  const image = watch('image');
  const previewImage = watch('previewImage');

  const {
    getRootProps,
    getInputProps,
    open,
    handleApplyImage,
    handlePreviewImageRemove,
  } = useStreamImageUpload({ setValue });

  return (
    <div className={styles.root}>
      {label && (
        <Text variant="body2Medium" color="lights-low" className={styles.label}>
          {label}
        </Text>
      )}
      {image ? (
        <div className={cn(styles.uploader, styles.filled)}>
          <EasyImageCrop
            className={styles.croppedImage}
            imageFile={image}
            onApplyImage={handleApplyImage}
            aspect={1}
          />
          <button
            type="button"
            className={styles.removeImageButton}
            aria-label="Remove image"
            onClick={handlePreviewImageRemove}
          >
            <MillionsIcon name="trash" size={24} />
          </button>
        </div>
      ) : previewImage ? (
        <div className={cn(styles.uploader, styles.filled)}>
          <img src={previewImage} className={styles.previewImage} alt={alt} />
          <button
            type="button"
            className={styles.removeImageButton}
            aria-label="Remove image"
            onClick={handlePreviewImageRemove}
          >
            <MillionsIcon name="trash" size={24} />
          </button>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={cn(styles.uploader, {
            [styles.uploaderError]: Boolean(errors?.previewImage),
          })}
        >
          <input {...getInputProps()} aria-label="upload image" />
          <UploadImagePlaceholder
            title="Drop an image or click to upload"
            description=".png or .jpeg max 50mb"
            onClick={open}
          />
        </div>
      )}
      {errors?.previewImage && image && (
        <Text variant="captionRegular" color="lights-medium">
          Apply your crop to set the poster
        </Text>
      )}
      {errors?.previewImage && !image && (
        <Text
          variant="captionRegular"
          className={styles.errorMessage}
          role="alert"
        >
          {(errors.previewImage as FieldError)?.message || 'Poster is required'}
        </Text>
      )}
    </div>
  );
};

export default PosterUploader;
